/**
 * Utility to parse and format JSON responses from the completion API
 * into markdown format for better user experience
 */

import { looksLikeHTML } from './html';
import { parseAgentScaffolding } from './agentScaffolding';

/**
 * Strips HTML tags from a string, returning plain text
 * @param {string} html - HTML string
 * @returns {string} - Plain text
 */
const stripHTML = (html) => {
  if (!html || typeof html !== 'string') return html;
  // Use DOMParser if available (browser environment)
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  } catch {
    // Fallback: simple regex strip
    return html.replace(/<[^>]+>/g, '').trim();
  }
};

/**
 * Parses a markdown table string and returns it formatted
 * @param {string} tableStr - Markdown table string
 * @returns {string} - Formatted markdown table
 */
const parseMarkdownTable = (tableStr) => {
  if (!tableStr) return '';
  
  // Ensure the table has proper line breaks
  return tableStr.trim();
};

/**
 * Extracts JSON from markdown code fences if present
 * @param {string} str - The string to extract JSON from
 * @returns {string} - The extracted JSON string or original string
 */
const extractJSONFromMarkdown = (str) => {
  const trimmed = str.trim();
  
  // Check for markdown code fence with json
  const jsonFenceMatch = trimmed.match(/^```(?:json)?\s*\n?([\s\S]*?)\n?```$/);
  if (jsonFenceMatch) {
    return jsonFenceMatch[1].trim();
  }
  
  return trimmed;
};

/**
 * Parses a JSON response and converts it to markdown format
 * @param {string|object} response - The API response (can be string or object)
 * @param {boolean} returnStructured - If true, returns structured data for tabs
 * @returns {string|object} - Formatted markdown content or structured object
 */
export const parseJSONResponse = (response, returnStructured = false) => {
  try {
    // Handle null or undefined
    if (!response) {
      return returnStructured ? null : '';
    }

    // If response is a string, try to parse it as JSON
    let data = response;
    if (typeof response === 'string') {
      // Extract JSON from markdown code fence if present
      const extracted = extractJSONFromMarkdown(response);
      
      // Only try to parse if it looks like JSON
      if (extracted.startsWith('{') || extracted.startsWith('[')) {
        data = JSON.parse(extracted);
      } else {
        return returnStructured ? null : response; // Return as-is if not JSON
      }
    }
    
    // Check if this is a JSON response that needs parsing
    if (typeof data !== 'object' || data === null) {
      return returnStructured ? null : (typeof response === 'string' ? response : JSON.stringify(response));
    }

    // Case 1: Ambiguous query with clarification question
    if (data.is_ambiguous || data.clarification_question) {
      const formatted = formatAmbiguousResponse(data);
      return returnStructured ? { description: formatted, hasTable: false } : formatted;
    }

    // Case 2: Response with key insights, answer summary, or data sources
    if (data.key_insights || data.actionable_recommendations || data.data_sources || data.answer_summary) {
      if (returnStructured) {
        return formatInsightsResponseStructured(data);
      }
      return formatInsightsResponse(data);
    }

    // If none of the known formats match, return JSON stringified
    return returnStructured ? null : JSON.stringify(data, null, 2);
  } catch (error) {
    // If parsing fails, return the original response
    console.error('Error parsing JSON response:', error);
    return returnStructured ? null : (typeof response === 'string' ? response : JSON.stringify(response));
  }
};

/**
 * Formats an ambiguous query response
 * @param {object} data - The parsed JSON data
 * @returns {string} - Formatted markdown
 */
const formatAmbiguousResponse = (data) => {
  let markdown = '';

  // Add clarification question (or fall back to key_insights if it's an HTML/text string)
  if (data.clarification_question) {
    markdown += `### 🤔 Clarification Needed\n\n`;
    markdown += `${data.clarification_question}\n\n`;
  } else if (data.key_insights && typeof data.key_insights === 'string') {
    markdown += `${stripHTML(data.key_insights)}\n\n`;
  } else {
    markdown += `### 🤔 Clarification Needed\n\n`;
  }

  // Add context information if available
  if (data.context) {
    if (data.context.original_query) {
      markdown += `**Your Query:** ${data.context.original_query}\n\n`;
    }

    if (data.context.sub_agent_message) {
      markdown += `**Note:** ${data.context.sub_agent_message}\n\n`;
    }

    // missing_info may be an array or a string; only list-render arrays
    const missingInfo = data.context.missing_info;
    if (Array.isArray(missingInfo) && missingInfo.length > 0) {
      markdown += `**Missing Information:**\n`;
      missingInfo.forEach(info => {
        markdown += `- ${info.replace(/_/g, ' ')}\n`;
      });
      markdown += `\n`;
    }
  }

  return markdown.trim();
};

/**
 * The "Data Sources" block of the text layout: one titled table (or JSON block) per source.
 * @param {object} dataSources - data_sources from the answer
 * @returns {string} - Markdown, ending in a blank line, or '' when there are none
 */
const formatDataSourcesMarkdown = (dataSources) => {
  if (!dataSources || Object.keys(dataSources).length === 0) return '';
  let markdown = `### 📊 Data Sources\n\n`;
  Object.entries(dataSources).forEach(([sourceName, tableData]) => {
    markdown += `#### ${sourceName.replace(/([A-Z])/g, ' $1').trim()}\n\n`;
    if (typeof tableData === 'string') {
      markdown += `${parseMarkdownTable(tableData)}\n\n`;
    } else {
      markdown += `\`\`\`json\n${JSON.stringify(tableData, null, 2)}\n\`\`\`\n\n`;
    }
  });
  return markdown;
};

/**
 * Keeps only the table-text data sources (strings); object values such as raw rows or
 * storage references would otherwise print as JSON blocks under a Markdown answer.
 * @param {object} dataSources - The answer's data_sources map
 * @returns {object}
 */
const stringSources = (dataSources) =>
  Object.fromEntries(Object.entries(dataSources || {}).filter(([, value]) => typeof value === 'string'));

/**
 * Whether this answer's key_insights is a complete Markdown answer.
 * discover tags answers that followed the agent's LLM Schema (completions V2) with
 * `answer_format: "markdown"`: the model already wrote the headings, tables and
 * lists, so it is shown as is — no "Key Insights" banner, numbering or period block.
 * Untagged answers (legacy, V1, older messages) keep the layout below.
 * @param {object} data - The parsed JSON data
 * @returns {boolean}
 */
export const isMarkdownAnswer = (data) =>
  !!data &&
  data.answer_format === 'markdown' &&
  typeof data.key_insights === 'string' &&
  data.key_insights.trim().length > 0;

/**
 * Formats a response with key insights and recommendations
 * @param {object} data - The parsed JSON data
 * @returns {string} - Formatted markdown
 */
const formatInsightsResponse = (data) => {
  // If key_insights is a pre-formatted HTML string, return it directly
  if (looksLikeHTML(data.key_insights)) {
    return data.key_insights;
  }

  if (isMarkdownAnswer(data)) {
    // Same data-source tables as the layout below, after the model's own answer.
    // Only table text: object sources (raw rows, storage references) would print as JSON blocks.
    const sources = formatDataSourcesMarkdown(stringSources(data.data_sources));
    return sources ? `${data.key_insights.trim()}\n\n${sources}`.trim() : data.key_insights.trim();
  }

  let markdown = '';

  // Add date range if available
  if (data.start_date || data.end_date) {
    markdown += `### 📅 Analysis Period\n\n`;
    if (data.start_date && data.end_date) {
      markdown += `**${data.start_date}** to **${data.end_date}**\n\n`;
    } else if (data.start_date) {
      markdown += `**From:** ${data.start_date}\n\n`;
    } else if (data.end_date) {
      markdown += `**To:** ${data.end_date}\n\n`;
    }
  }

  // Add key insights
  const insightsArray = Array.isArray(data.key_insights)
    ? data.key_insights
    : typeof data.key_insights === 'string' && data.key_insights.trim()
      ? [data.key_insights.trim()]
      : [];
  if (insightsArray.length > 0) {
    markdown += `### 💡 Key Insights\n\n`;
    insightsArray.forEach((insight, index) => {
      // Replace <link> tags with inline references
      const formattedInsight = insight.replace(/<link>(.*?)<\/link>/g, '`$1`');
      markdown += `${index + 1}. ${formattedInsight}\n\n`;
    });
  }

  // Add data sources (tables)
  markdown += formatDataSourcesMarkdown(data.data_sources);

  // Add actionable recommendations
  const recommendationsArray = Array.isArray(data.actionable_recommendations)
    ? data.actionable_recommendations
    : typeof data.actionable_recommendations === 'string' && data.actionable_recommendations.trim()
      ? [data.actionable_recommendations.trim()]
      : [];
  if (recommendationsArray.length > 0) {
    markdown += `### 🎯 Actionable Recommendations\n\n`;
    recommendationsArray.forEach((recommendation, index) => {
      // Extract bold headers if present (lines starting with **)
      const formattedRecommendation = recommendation.replace(/<link>(.*?)<\/link>/g, '`$1`');
      markdown += `${index + 1}. ${formattedRecommendation}\n\n`;
    });
  }

  return markdown.trim();
};

/**
 * A data-source key, as a heading.
 *
 * The keys the agent sends are already written for a reader ("Step 1 -
 * Retrieving CMS Data"); only a camelCase key needs splitting. The previous rule
 * spaced every capital, which turned that key into "Retrieving C M S Data".
 * Splitting only at a lower-to-upper or acronym-to-word boundary leaves prose and
 * acronyms intact, and a key that already contains a space is left alone
 * entirely.
 */
const humanizeSourceName = (name) => {
  const text = String(name ?? '').trim();
  if (!text || /\s/.test(text)) return text;
  return text
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
};

const buildTableDataString = (dataSources) => {
  if (!dataSources || Object.keys(dataSources).length === 0) return '';
  let result = '';
  Object.entries(dataSources).forEach(([sourceName, val]) => {
    const heading = `### ${humanizeSourceName(sourceName)}\n\n`;
    if (typeof val === 'string' && val.trim()) {
      result += heading + `${val}\n\n`;
    } else if (Array.isArray(val) && val.length > 0) {
      result += heading + `${JSON.stringify(val)}\n\n`;
    }
  });
  return result;
};

/**
 * Formats a response with key insights and recommendations into structured data
 * @param {object} data - The parsed JSON data
 * @returns {object} - Structured object with description and tableData
 */
const formatInsightsResponseStructured = (data) => {
  let tableData = '';

  // If key_insights is a pre-formatted HTML string, return it directly
  if (looksLikeHTML(data.key_insights)) {
    // Extract data sources (tables) still needed for the data tab
    tableData = buildTableDataString(data.data_sources);
    return {
      description: data.key_insights,
      descriptionIsHtml: true,
      tableData: tableData.trim(),
      dataSources: data.data_sources || null,
      hasTable: tableData.trim().length > 0,
      datatype: 'insights'
    };
  }

  if (isMarkdownAnswer(data)) {
    tableData = buildTableDataString(data.data_sources);
    return {
      description: data.key_insights.trim(),
      tableData: tableData.trim(),
      dataSources: data.data_sources || null,
      hasTable: tableData.trim().length > 0,
      datatype: 'insights',
    };
  }

  let description = '';

  // Fast mode: answer_summary replaces key_insights/actionable_recommendations
  if (data.answer_summary && typeof data.answer_summary === 'string' && data.answer_summary.trim()) {
    // A producer whose own decision parser failed can send its raw ReAct
    // transcript here instead of an answer. Salvage the answer out of it and
    // hand the thoughts/tool calls back as timeline steps, so the user sees a
    // reply and an activity trail rather than a wall of JSON. Clean text passes
    // through untouched.
    const salvaged = parseAgentScaffolding(data.answer_summary.trim());
    description = salvaged.answer;
    // Still extract data sources for the Data tab
    tableData = buildTableDataString(data.data_sources);
    return {
      description: description,
      tableData: tableData.trim(),
      dataSources: data.data_sources || null,
      hasTable: tableData.trim().length > 0,
      datatype: 'insights',
      isSummary: true,
      activitySteps: salvaged.steps,
    };
  }

  // Add date range if available
  if (data.start_date || data.end_date) {
    description += `### 📅 Analysis Period\n\n`;
    if (data.start_date && data.end_date) {
      description += `**${data.start_date}** to **${data.end_date}**\n\n`;
    } else if (data.start_date) {
      description += `**From:** ${data.start_date}\n\n`;
    } else if (data.end_date) {
      description += `**To:** ${data.end_date}\n\n`;
    }
  }

  // Add key insights
  const insightsArray = Array.isArray(data.key_insights)
    ? data.key_insights
    : typeof data.key_insights === 'string' && data.key_insights.trim()
      ? [data.key_insights.trim()]
      : [];
  if (insightsArray.length > 0) {
    description += `### 💡 Key Insights\n\n`;
    insightsArray.forEach((insight, index) => {
      const formattedInsight = insight.replace(/<link>(.*?)<\/link>/g, '`$1`');
      description += `${index + 1}. ${formattedInsight}\n\n`;
    });
  }

  // Add actionable recommendations
  const recommendationsArray = Array.isArray(data.actionable_recommendations)
    ? data.actionable_recommendations
    : typeof data.actionable_recommendations === 'string' && data.actionable_recommendations.trim()
      ? [data.actionable_recommendations.trim()]
      : [];
  if (recommendationsArray.length > 0) {
    description += `### 🎯 Actionable Recommendations\n\n`;
    recommendationsArray.forEach((recommendation, index) => {
      const formattedRecommendation = recommendation.replace(/<link>(.*?)<\/link>/g, '`$1`');
      description += `${index + 1}. ${formattedRecommendation}\n\n`;
    });
  }

  // Extract data sources (tables) — supports string (markdown) and array (JSON rows)
  tableData = buildTableDataString(data.data_sources);

  return {
    description: description.trim(),
    tableData: tableData.trim(),
    dataSources: data.data_sources || null,
    hasTable: tableData.trim().length > 0,
    datatype: 'insights'
  };
};

/**
 * Processes a JSON response content (answer/msg.content) into UI-ready fields.
 * Determines the correct datatype, finalMessage, and finalDescription depending
 * on whether the structured response has table data, clarification options, or insights.
 *
 * Returns null when rawContent is not a recognised JSON response (caller handles that case).
 *
 * @param {string|object} rawContent - The raw answer or msg.content to process
 * @param {boolean} hasClarificationOptions - Whether the response includes clarification options
 * @returns {{ datatype: string|null, finalMessage: any, finalDescription: string|null }|null}
 */
export const processJSONContent = (rawContent, hasClarificationOptions = false) => {
  if (!rawContent || !isJSONResponse(rawContent)) {
    return null;
  }
  const structured = parseJSONResponse(rawContent, true);
  if (structured && structured.hasTable) {
    // Has data sources – split across tabs
    return {
      datatype: 'insights',
      finalMessage: structured.tableData,
      finalDescription: structured.description || '',
      dataSources: structured.dataSources || null,
      isSummary: structured.isSummary || false,
      activitySteps: structured.activitySteps || null,
    };
    // `isSummary` without a description means a salvage that recovered no
    // answer text. Keep it on the insights path anyway — falling through to the
    // raw representation below would put the machinery back on screen, which is
    // the thing the salvage exists to prevent.
  } else if (structured && (structured.description || structured.isSummary)) {
    if (hasClarificationOptions) {
      // Ambiguous response – keep as text so ClarificationOptions render in the Data tab
      return {
        datatype: 'text',
        finalMessage: structured.description,
        finalDescription: '',
      };
    } else {
      // Insights/recommendations with no table data
      return {
        datatype: 'insights',
        finalMessage: '',
        finalDescription: structured.description,
        isSummary: structured.isSummary || false,
        activitySteps: structured.activitySteps || null,
      };
    }
  } else {
    // Fallback: plain-text representation
    return {
      datatype: null,
      finalMessage: parseJSONResponse(rawContent),
      finalDescription: null,
    };
  }
};

/**
 * Checks if a message is a JSON response that needs parsing
 * @param {string|object} message - The message to check
 * @returns {boolean} - True if it's a JSON response
 */
export const isJSONResponse = (message) => {
  // Handle null or undefined
  if (!message) return false;

  // Check if it's already an object
  if (typeof message === 'object') {
    return message.is_ambiguous !== undefined ||
           message.key_insights !== undefined ||
           message.actionable_recommendations !== undefined ||
           message.data_sources !== undefined ||
           message.answer_summary !== undefined;
  }

  // Check if it's a string that looks like JSON
  if (typeof message === 'string') {
    const trimmed = message.trim();
    
    // Check for markdown code fence with JSON
    const jsonFenceMatch = trimmed.match(/^```(?:json)?\s*\n?([\s\S]*?)\n?```$/);
    if (jsonFenceMatch) {
      const extracted = jsonFenceMatch[1].trim();
      try {
        const parsed = JSON.parse(extracted);
        if (typeof parsed !== 'object' || parsed === null) {
          return false;
        }
        return parsed.is_ambiguous !== undefined ||
               parsed.key_insights !== undefined ||
               parsed.actionable_recommendations !== undefined ||
               parsed.data_sources !== undefined ||
               parsed.answer_summary !== undefined;
      } catch {
        return false;
      }
    }

    // Check if string starts with { or [ (likely JSON)
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed !== 'object' || parsed === null) {
          return false;
        }
        return parsed.is_ambiguous !== undefined ||
               parsed.key_insights !== undefined ||
               parsed.actionable_recommendations !== undefined ||
               parsed.data_sources !== undefined ||
               parsed.answer_summary !== undefined;
      } catch {
        return false;
      }
    }
  }

  return false;
};

/**
 * Extracts key_insights from an unknown-type response as a plain joined string.
 * Returns null when no insights are found so callers can skip the override.
 *
 * @param {string|object} rawContent - The raw answer/content value
 * @returns {string|null}
 */
export const extractUnknownTypeInsights = (rawContent) => {
  let parsed = rawContent;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch { return null; }
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const rawInsights = parsed.key_insights;
  if (!rawInsights) return null;
  const insights = Array.isArray(rawInsights)
    ? rawInsights
    : (typeof rawInsights === 'string' && rawInsights.trim() ? [rawInsights.trim()] : []);
  return insights.length > 0 ? insights.join('\n\n') : null;
};
